<template>
  <div class="KlarnaCheckout">
    <!-- Loading state -->
    <div v-if="isLoading" class="klarna-loading flex items-center justify-center py-32">
      <div class="loading-spinner" />
      <span class="ml-12 type-sm text-darker">{{ loadingText }}</span>
    </div>

    <!-- Error state -->
    <div v-else-if="hasError" class="klarna-error p-16 bg-criticalLight border border-criticalDark">
      <p class="type-sm text-criticalDark">{{ errorMessage }}</p>
      <button
        v-if="showRetry"
        class="btn btn--sm mt-12"
        type="button"
        @click="$emit('retry')"
      >
        {{ retryText }}
      </button>
    </div>

    <!-- Klarna Checkout container -->
    <div v-else class="klarna-wrapper relative">
      <div
        ref="klarnaContainer"
        class="klarna-container"
      />

      <!-- Validation Overlay - blocks interaction until checkout requirements are met -->
      <div
        v-if="!canCompletePurchase"
        class="klarna-validation-overlay"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import type { KlarnaPaymentData } from './types';
import { savePaymentSession } from './usePaymentSession';

const props = withDefaults(defineProps<{
  klarnaPayment: KlarnaPaymentData | null;
  basketUuid?: string;
  orderId?: string;
  paymentId?: string;
  isLoading?: boolean;
  loadingText?: string;
  errorMessage?: string;
  showRetry?: boolean;
  retryText?: string;
  canCompletePurchase?: boolean;
}>(), {
  basketUuid: '',
  orderId: '',
  paymentId: '',
  isLoading: false,
  loadingText: 'Loading Klarna...',
  errorMessage: '',
  showRetry: true,
  retryText: 'Try again',
  canCompletePurchase: true,
});

/**
 * Save current payment session for Klarna redirect flow
 * Klarna redirects to payment-redirect without query params after completion
 */
const saveCurrentSession = () => {
  savePaymentSession({
    orderId: props.orderId || undefined,
    paymentId: props.paymentId || props.klarnaPayment?.paymentId || undefined,
    basketUuid: props.basketUuid || undefined,
    paymentAdapter: 'klarna',
  });
};

const emit = defineEmits<{
  (e: 'retry'): void;
  (e: 'loaded'): void;
  (e: 'error', message: string): void;
}>();

const klarnaContainer = ref<HTMLElement | null>(null);
const hasError = computed(() => Boolean(props.errorMessage));

/**
 * Render the Klarna Checkout widget
 * Klarna's HTML snippet contains all necessary JS and handles the checkout flow
 *
 * Note: innerHTML doesn't execute script tags for security reasons.
 * Following Klarna's official documentation pattern:
 * https://docs.kustom.co/contents/checkout/integrate-kco-in-your-ecommerce/render-checkout-snippet
 */
const renderKlarnaCheckout = async() => {
  if (!props.klarnaPayment?.htmlSnippet || !klarnaContainer.value) {
    return;
  }

  try {
    // Clear previous content
    klarnaContainer.value.innerHTML = '';

    // Step 1: Insert the HTML snippet (scripts will be in DOM but not executed)
    klarnaContainer.value.innerHTML = props.klarnaPayment.htmlSnippet;

    // Step 2: Get all script tags from the container
    // Convert to static array since getElementsByTagName returns a live collection
    // that would cause infinite loop when we add new scripts
    const scriptTags = Array.from(klarnaContainer.value.getElementsByTagName('script'));

    // Step 3: Properly parse and execute embedded script tags
    for (const oldScript of scriptTags) {
      const parentNode = oldScript.parentNode;

      if (!parentNode) { continue; }

      // Create a new script element that will actually execute
      const newScript = document.createElement('script');
      newScript.type = 'text/javascript';
      newScript.text = oldScript.text;

      // Copy any attributes (like id, src, etc.)
      Array.from(oldScript.attributes).forEach((attr) => {
        if (attr.name !== 'type') {
          newScript.setAttribute(attr.name, attr.value);
        }
      });

      // Remove the old (inert) script and append the new (active) one
      parentNode.removeChild(oldScript);
      parentNode.appendChild(newScript);
    }

    await nextTick();
    emit('loaded');
  } catch (e) {
    console.error('Klarna Checkout render error:', e);
    emit('error', 'Failed to load Klarna Checkout');
  }
};

/**
 * Suspend Klarna Checkout
 * Call this before making changes to the order (e.g., cart updates)
 */
const suspend = () => {
  if (typeof window !== 'undefined' && (window as any)._klarnaCheckout) {
    try {
      (window as any)._klarnaCheckout((api: any) => {
        api.suspend();
      });
    } catch (e) {
      console.warn('Failed to suspend Klarna Checkout:', e);
    }
  }
};

/**
 * Resume Klarna Checkout
 * Call this after order changes are complete
 */
const resume = () => {
  if (typeof window !== 'undefined' && (window as any)._klarnaCheckout) {
    try {
      (window as any)._klarnaCheckout((api: any) => {
        api.resume();
      });
    } catch (e) {
      console.warn('Failed to resume Klarna Checkout:', e);
    }
  }
};

// Watch for changes in Klarna payment data
watch(
  () => props.klarnaPayment,
  async(newPayment) => {
    if (newPayment?.htmlSnippet) {
      // Wait for next tick to ensure ref is available after mount
      await nextTick();
      renderKlarnaCheckout();
    }
  },
  { immediate: true }
);

// Also render on mount if data is already available
// This handles the case when the component mounts with data already present
onMounted(async() => {
  if (props.klarnaPayment?.htmlSnippet) {
    // Save session for Klarna redirect flow
    saveCurrentSession();
    await nextTick();
    renderKlarnaCheckout();
  }
});

// Cleanup on unmount
onBeforeUnmount(() => {
  // Clear the container to prevent memory leaks
  if (klarnaContainer.value) {
    klarnaContainer.value.innerHTML = '';
  }
});

// Expose methods for parent component
defineExpose({
  suspend,
  resume,
  renderKlarnaCheckout,
});
</script>

<style scoped lang="postcss">
.KlarnaCheckout {
  @apply w-full;
}

.klarna-wrapper {
  @apply relative;
}

.klarna-container {
  @apply w-full min-h-[200px];
}

/* Validation overlay - blocks interaction until checkout requirements are met */
.klarna-validation-overlay {
  @apply absolute inset-0 cursor-not-allowed z-10;
  background-color: rgba(255, 255, 255, 0.6);
}

.klarna-loading {
  @apply min-h-[200px];
}

.loading-spinner {
  @apply w-24 h-24 border-2 border-light border-t-dark animate-spin;
  border-radius: 50%;
}
</style>
